/**
 * FavoriteStarButton — standalone star toggle for a single favorite value.
 *
 * Mirrors the star control inside FavoritesRail but renders without the
 * accompanying pill strip, so it can be placed inline next to a TextField
 * (e.g. the Current Location input). Both this button and the rail share
 * the same `useFieldFavorites` context provider, so toggling here is
 * immediately reflected in the rail's saved-pill set.
 */

import React from 'react';
import { IconButton, Tooltip } from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { FieldFavoriteType } from '../../utils/fieldFavoritesApi';
import { useFieldFavorites } from '../../hooks/useFieldFavorites';
import { logger } from '../../utils/logger';

interface FavoriteStarButtonProps {
  type: FieldFavoriteType;
  currentValue: string;
  /** Optional override for the IconButton size — defaults to 'small'. */
  size?: 'small' | 'medium';
}

export const FavoriteStarButton: React.FC<FavoriteStarButtonProps> = ({
  type,
  currentValue,
  size = 'small',
}) => {
  const ctx = useFieldFavorites();
  if (!ctx) return null;
  const { isFavorite, addFavorite, removeFavorite } = ctx;
  const trimmed = currentValue.trim();
  const currentSaved = trimmed.length > 0 && isFavorite(type, trimmed);

  const handleClick = async () => {
    if (!trimmed) return;
    try {
      if (currentSaved) {
        await removeFavorite(type, trimmed);
      } else {
        await addFavorite(type, trimmed);
      }
    } catch (error) {
      // Surface the failure so the user-visible state isn't quietly out of
      // sync with persisted favorites. Errors here are network/auth failures
      // from the favorites API; the underlying provider already reverts
      // optimistic state on rejection.
      logger.error('Failed to toggle favorite', {
        type,
        action: currentSaved ? 'remove' : 'add',
        error,
      });
    }
  };

  const title = currentSaved ? 'Unsave from favorites' : 'Save current as favorite';
  const icon = currentSaved ? <StarIcon fontSize={size} /> : <StarBorderIcon fontSize={size} />;

  return (
    <Tooltip title={title}>
      <span>
        <IconButton
          size={size}
          onClick={() => { void handleClick(); }}
          disabled={!trimmed}
          color={currentSaved ? 'warning' : 'default'}
          aria-label={title}
          data-testid={`favorite-star-${type}`}
        >
          {icon}
        </IconButton>
      </span>
    </Tooltip>
  );
};
